/**
 * Popup (dialog block)
 *
 * =============================================================================
 * @example Bind invoke popup to DOM element (by click)
 * ---
 * html:
 * <button type="button" class="js-popup" data-popup-target="login">Login</button>
 *
 * javascript:
 * const popup = new Popup( '.js-popup', {
 *  // optional
 *  activeClassName: 'is-open',
 *  closeBtn: '.js-popup-close',
 *  contentBox: '.popup__body',
 * } );
 *
 * =============================================================================
 * @example Show popup by script (if another popup is visible one will be hide)
 * ---
 * const eventPopupShowSuccess =
 *        new CustomEvent( 'popup.show', { detail: { name: 'success' } } );
 * document.dispatchEvent( eventPopupShowSuccess );
 *
 * =============================================================================
 * @example Hide popup by script
 * ---
 * const eventPopupHide = new CustomEvent( 'popup.hide' );
 * document.dispatchEvent( eventPopupHide );
 */

import '../vendor/polyfills/classList';
import '../vendor/polyfills/core-js.es6.symbol';
import '../vendor/polyfills/custom-event-polyfill';
import '../vendor/polyfills/es6-assign';
import '../vendor/polyfills/from';
import '../vendor/polyfills/includes';

import jqueryHelper from './webix/jqueryHelper';
import datePcker from './webix/datePicker';
import select from './webix/select';

const defaults = {
  activeClassName: 'is-open',
  closeBtn: '.js-popup-close',
  contentBox: '.popup__body',
};

// helpers
const $ = ( selector, context = document ) => context.querySelector( selector );
const $$ = ( selector, context = document ) => context.querySelectorAll( selector );

const $body = $( 'body' );

const INIT_METHOD = Symbol( '@@init method' );
const SHOW_METHOD = Symbol( '@@show method' );
const HIDE_METHOD = Symbol( '@@hide method' );

const SHOW_EVENT = 'popup.show';
const HIDE_EVENT = 'popup.hide';
const KEY_ESC = 27;
const instances = [];
const $popupAjax = jQuery( `<div class="popup" data-popup-name="ajax">
  <section class="popup__body">
    <button class="popup__close js-popup-close" type="button" title="Закрыть" aria-label="Закрыть">
      <svg width="34" height="34"><use xlink:href="/img/sprite.svg#close"></use></svg>
    </button>
    
    <div class="popup__content"></div>
  </section>
</div>` );

let $popupActive = null;
let $popupCloseBtn = null;
let $popupContentBox = null;

jQuery( 'body' ).append( $popupAjax );

class Popup {

  /**
   * Constructor
   *
   * @constructor
   * @param {string} selector - css selector
   * @param {object} options - popup options [optional]
   * @example
   * const popup = new Popup( '.js-popup' );
   */
  constructor( selector, options = {} ) {
    // one instance for the css selector
    if ( !instances.includes( selector ) ) {
      instances.push( selector );
      this[ INIT_METHOD ]( selector, options );
    }
  }

  // ***************
  // PUBLIC METHODS
  // ***************

  // ***************
  // PRIVATE METHODS
  // ***************
  /**
   * Initialize the instance
   *
   * @param {string} selector - css selector
   * @param {object} options - popup options
   *
   * @return {null} - nothing
   */
  [ INIT_METHOD ]( selector, options ) {
    this.options = Object.assign( {}, defaults, options );

    this.hide = ( ev ) => this[ HIDE_METHOD ]( ev );
    this.show = ( ev ) => this[ SHOW_METHOD ]( ev );

    // buttons click
    [ ...$$( selector ) ].forEach( ( btn ) => {
      btn.addEventListener( 'click', this.show );
    } );

    // show popup on document custom event 'popup.show'
    document.addEventListener( SHOW_EVENT, this.show );
    // hide popup on document custom event 'popup.hide'
    document.addEventListener( HIDE_EVENT, this.hide );
  }

  /**
   * show popup block
   *
   * @param {object} ev - Event()
   * @return {Popup} - Popup object
   */
  [ SHOW_METHOD ]( ev ) {
    let btn = null;
    let popupTarget = null;
    let popupUrl = null;
    let $popup = null;
    let $ajaxContent = null;
    let formSubject = null;

    ev.preventDefault();

    if ( ev.isTrusted === true ) {
      // if event dispatched by user
      btn = ev.target;
      popupTarget = btn.getAttribute( 'data-popup-target' );
      popupUrl = btn.getAttribute( 'data-popup-url' );
      formSubject = btn.getAttribute( 'data-form-subject' );
    } else {
      // if event dispatched by script
      popupTarget = ev.detail.name;
    }

    if ( popupUrl !== null ) {
      // ajax content
      $popup = $( '[data-popup-name=ajax]' );
      $popup.classList.add( 'is-loading' );
      $ajaxContent = $popup.querySelector( '.popup__content' );

      jQuery.ajax( {
        url: popupUrl,
        success( data ) {
          jQuery($ajaxContent).html(data);

          jqueryHelper();
          datePcker( jQuery( $popup ) );
          select( jQuery( $popup ) );
        },
        error() {
          $ajaxContent.innerHTML = '<p class="u-color-highlight u-pt u-pl u-pr u-pb u-text-center">Ошибка загрузки</p>';
        },
        complete() {
          $popup.classList.remove( 'is-loading' );
        },
      } );
    } else if ( popupTarget !== null ) {
      // find DOM popup-element
      $popup = $( `[data-popup-name=${ popupTarget }]` );
    }

    if ( $popup !== null ) {
      // if visible another popup then hide it
      this.hide();

      $popupActive = $popup;
      $popup.classList.add( this.options.activeClassName );

      // close button
      $popupCloseBtn = $popup.querySelector( this.options.closeBtn );

      // close popup by click outside an popup content (on fade)
      $popupContentBox = $popup.querySelector( this.options.contentBox );
      $popup.addEventListener( 'click', this.hide );

      // close popup by press ESC button
      document.addEventListener( 'keyup', this.hide );


      // !!! No need for popup functionality
      // Set subject for an inside form
      if ( formSubject !== null ) {
        const subject = $popup.querySelector( 'input[name=_subject]' );

        if ( subject !== null ) { subject.value = formSubject; }
      }

      $body.style.overflow = 'hidden';
    }

    return this;
  }

  /**
   * Hide popup block
   *
   * @param {object} ev - Event()
   * @return {Popup} - Popup object
   */
  [ HIDE_METHOD ]( ev ) {
    // if no visible popup then skip process
    if ( $popupActive === null ) { return this; }

    // if ESC button pressed
    if ( typeof ev !== 'undefined' && ev.type === 'keyup' ) {
      const code = ev.keyCode || ev.which;

      if ( code !== KEY_ESC ) { return this; }
    }

    if ( typeof ev !== 'undefined' && ev.isTrusted === true ) {
      // if clicked outside the popup content (on fade)
      // and if NOT clicked the popup close button
      const isInsideClick = $popupContentBox !== null && $popupContentBox.contains( ev.target );
      const isCloseBtnClick = $popupCloseBtn !== null && $popupCloseBtn.contains( ev.target );

      if ( isInsideClick === true && isCloseBtnClick !== true ) { return this; }
    }

    // remove document listeners
    document.removeEventListener( 'keyup', this.hide );

    // remove popup listeners
    $popupActive.removeEventListener( 'click', this.hide );

    // remove active class name
    $popupActive.classList.remove( this.options.activeClassName, 'is-loading' );

    $popupActive = null;
    $popupCloseBtn = null;
    $popupContentBox = null;

    $body.style.overflow = 'auto';

    return this;
  }
}

export default Popup;
